import { z } from 'zod'

// Tracking (#30): GA4, Meta Pixel + Conversions API, TikTok Pixel + Events API. Admins enter every
// ID and credential in the CMS. Public IDs (measurement id, pixel id, pixel code) reach the browser;
// tokens and secrets are write-only: stored encrypted, never returned, only `configured` + last 4.

export const TRACKING_PLATFORMS = ['ga', 'meta', 'tiktok'] as const
export const TrackingPlatformSchema = z.enum(TRACKING_PLATFORMS)
export type TrackingPlatform = z.infer<typeof TrackingPlatformSchema>

const Enabled = z.boolean()
const measurementId = z.string().trim().regex(/^(G-[A-Z0-9]{4,20})?$/, 'Looks like G-XXXXXXXXXX')
const metaPixelId = z.string().trim().regex(/^\d{0,20}$/, 'Digits only')
const tiktokPixelCode = z.string().trim().regex(/^[A-Za-z0-9]{0,40}$/, 'Letters and digits only')
const testCode = z.string().trim().regex(/^[A-Za-z0-9_-]{0,40}$/, 'Letters, digits, dashes')

/** The non-secret half, as stored and as the admin form edits it. */
export const TrackingConfigSchema = z.object({
  ga: z.object({ enabled: Enabled, measurementId }),
  meta: z.object({ enabled: Enabled, pixelId: metaPixelId, testEventCode: testCode }),
  tiktok: z.object({ enabled: Enabled, pixelCode: tiktokPixelCode, testEventCode: testCode }),
})
export type TrackingConfig = z.infer<typeof TrackingConfigSchema>
export const emptyTrackingConfig = (): TrackingConfig => ({
  ga: { enabled: false, measurementId: '' },
  meta: { enabled: false, pixelId: '', testEventCode: '' },
  tiktok: { enabled: false, pixelCode: '', testEventCode: '' },
})

/** How the admin changes a stored secret: keep it, replace it, or remove it. */
export const SecretPatchSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('keep') }),
  z.object({ action: z.literal('set'), value: z.string().trim().min(8).max(512) }),
  z.object({ action: z.literal('clear') }),
])
export type SecretPatch = z.infer<typeof SecretPatchSchema>

/** PUT body: the config plus one secret patch per platform. */
export const TrackingUpdateSchema = TrackingConfigSchema.extend({
  ga: TrackingConfigSchema.shape.ga.extend({ apiSecret: SecretPatchSchema }),
  meta: TrackingConfigSchema.shape.meta.extend({ accessToken: SecretPatchSchema }),
  tiktok: TrackingConfigSchema.shape.tiktok.extend({ accessToken: SecretPatchSchema }),
})
export type TrackingUpdate = z.infer<typeof TrackingUpdateSchema>

/** What the API reveals about a stored secret. */
export const SecretStatusSchema = z.object({ configured: z.boolean(), last4: z.string() })
export type SecretStatus = z.infer<typeof SecretStatusSchema>

/** GET response: config plus secret status (never a secret). */
export const TrackingViewSchema = TrackingConfigSchema.extend({
  ga: TrackingConfigSchema.shape.ga.extend({ apiSecret: SecretStatusSchema }),
  meta: TrackingConfigSchema.shape.meta.extend({ accessToken: SecretStatusSchema }),
  tiktok: TrackingConfigSchema.shape.tiktok.extend({ accessToken: SecretStatusSchema }),
})
export type TrackingView = z.infer<typeof TrackingViewSchema>

/** IDs the browser needs, empty when a platform is disabled or unconfigured. */
export const PublicTrackingSchema = z.object({
  gaMeasurementId: z.string(),
  /** True when the server sends GA4's generate_lead itself, so the browser must not (GA4 cannot de-duplicate). */
  gaServerLead: z.boolean(),
  metaPixelId: z.string(),
  tiktokPixelCode: z.string(),
})
export type PublicTracking = z.infer<typeof PublicTrackingSchema>

export const TrackingDeliverySchema = z.object({
  id: z.string(),
  platform: TrackingPlatformSchema,
  event: z.string(),
  status: z.enum(['ok', 'error']),
  httpStatus: z.number().int().nullable(),
  /** Short machine code ("http_400", "network", "timeout"); never a response body. */
  errorCode: z.string(),
  test: z.boolean(),
  createdAt: z.string(),
})
export type TrackingDelivery = z.infer<typeof TrackingDeliverySchema>
export const TrackingDeliveriesSchema = z.object({ items: z.array(TrackingDeliverySchema) })

/** Result of "Send test event". */
export const TrackingTestResultSchema = z.object({ ok: z.boolean(), httpStatus: z.number().int().nullable(), errorCode: z.string() })
export type TrackingTestResult = z.infer<typeof TrackingTestResultSchema>

/**
 * Attribution the web worker passes with an inquiry so the API can send the server copy of the
 * Lead. All optional: a submission without them still works, it just matches worse.
 */
export const InquiryTrackingSchema = z.object({
  /**
   * The visitor's answer in the site's consent bar. `false`: the enquiry is not reported to GA4, Meta or TikTok
   * (browser or server). Absent (a form without a consent bar) is treated as before: reported.
   */
  consent: z.boolean().optional(),
  /** Shared with the browser events: Meta and TikTok drop the second copy of the same id. */
  eventId: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/).optional(),
  fbp: z.string().max(200).optional(),
  fbc: z.string().max(400).optional(),
  ttclid: z.string().max(400).optional(),
  ttp: z.string().max(200).optional(),
  gaClientId: z.string().max(100).optional(),
  pageUrl: z.string().max(1000).optional(),
  referrer: z.string().max(1000).optional(),
  clientIp: z.string().max(64).optional(),
  userAgent: z.string().max(500).optional(),
})
export type InquiryTracking = z.infer<typeof InquiryTrackingSchema>
